import {
  COLORS,
  type TableViewCellDto,
  type TableViewColumnDto,
} from '@ditto/shared';
import { Calendar, Hash, MessageSquareText, Tags, Type } from 'lucide-react';

const KEYWORD_COLORS: Record<string, (typeof COLORS)[number]> = {
  yes: 'green',
  include: 'green',
  no: 'red',
  exclude: 'red',
};

export const getColorForOptionValue = (
  value: string
): (typeof COLORS)[number] | undefined => {
  return KEYWORD_COLORS[value.toLowerCase().trim()];
};

export const getColorForOption = (
  optionId: string,
  columns: TableViewColumnDto[]
): (typeof COLORS)[number] => {
  const allOptions = columns
    .filter(c => c.type === 'options')
    .flatMap(c => c.options ?? []);
  const option = allOptions.find(o => o.id === optionId);
  if (option) {
    const keywordColor = getColorForOptionValue(option.value);
    if (keywordColor) return keywordColor;
    const optionIndex = allOptions.indexOf(option);
    return COLORS[optionIndex % COLORS.length];
  }
  return COLORS[0];
};

export const columnTypeIcon = (type: TableViewColumnDto['type']) => {
  if (type === 'text') return Type;
  if (type === 'number') return Hash;
  if (type === 'date') return Calendar;
  if (type === 'verbatim') return MessageSquareText;
  if (type === 'options') return Tags;
  return null;
};

export const columnTypeTooltip = (type: TableViewColumnDto['type']) => {
  if (type === 'text') return 'Text';
  if (type === 'number') return 'Number';
  if (type === 'date') return 'Date';
  if (type === 'verbatim') return 'Verbatim';
  if (type === 'options') return 'Tags';
  return '';
};

export const getDisplayValue = (
  cell: TableViewCellDto,
  column: TableViewColumnDto
): string | undefined => {
  if (column.type === 'text') return cell.textValue || 'No value extracted';
  if (column.type === 'number')
    return cell.numberValue ? String(cell.numberValue) : 'No value extracted';
  if (column.type === 'date')
    return cell.dateValue
      ? new Date(cell.dateValue).toLocaleDateString()
      : 'No value extracted';
  if (column.type === 'verbatim')
    return cell.verbatimValue
      ? `"${cell.verbatimValue}"`
      : 'No value extracted';
  if (column.type === 'options')
    return cell.selectedOptions
      ? cell.selectedOptions.map(option => option.value).join(', ')
      : 'No value extracted';
  return undefined;
};

export const getCellRawValue = (
  cell: TableViewCellDto,
  column: TableViewColumnDto
): string | number | null => {
  if (column.type === 'text') return cell.textValue ?? null;
  if (column.type === 'number') return cell.numberValue ?? null;
  if (column.type === 'date') return cell.dateValue ?? null;
  if (column.type === 'verbatim') return cell.verbatimValue ?? null;
  return null;
};
